import { CircularProgress, Stack, Typography } from "@mui/material";
import { useState } from "react";
import RequiredFieldMarker from "@entrilia/shared/components/RequiredFieldMarker";
import HtmlEditor from "@entrilia/shared/components/html/HtmlEditor";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import InvestorCommunicationContext from "../InvestorCommunicationContext";
import type { ArticleEditState } from "../investorCommunicationArticleEditState";
import { setHtmlBodyAction } from "../investorCommunicationArticleEditState";
import { isContentStepCompleted } from "../investorCommunicationEditState";
import InvestorCommunicationContentFormFields from "./InvestorCommunicationContentFormFields";
import InvestorCommunicationContentFormHeader from "./InvestorCommunicationContentFormHeader";
import InvestorCommunicationContentPreview from "./InvestorCommunicationContentPreview";
import InvestorCommunicationContentPreviewDialog from "./InvestorCommunicationContentPreviewDialog";
import InvestorCommunicationImageField from "./InvestorCommunicationImageField";

const heroImageHint = "Recommended 1200 × 800 px (3:2). PNG, JPG or WEBP, up to 25 MB.";

interface Props {
  articleState: ArticleEditState;
}

const InvestorCommunicationArticleContentForm = ({ articleState }: Props) => {
  const {
    state,
    setState,
    isSaving,
    hasPendingImageUploads,
    draftHeroImageSource,
    validationErrors,
    onHeroImageFileAdd,
    onHeroImageRemove,
    onBodyImagesUpload,
  } = useDefinedContext(InvestorCommunicationContext, "InvestorCommunicationContext");

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  return (
    <Stack spacing={2.5}>
      <InvestorCommunicationContentFormHeader
        title="Article content"
        previewDisabled={isSaving || !isContentStepCompleted(state)}
        onPreviewClick={() => setIsPreviewOpen(true)}
      />

      <InvestorCommunicationContentFormFields />

      <InvestorCommunicationImageField
        title="Hero image"
        hint={heroImageHint}
        type={state.savedCommunication.type}
        source={draftHeroImageSource}
        disabled={isSaving}
        onFileAdd={onHeroImageFileAdd}
        onRemove={onHeroImageRemove}
      />

      <Stack spacing={1}>
        <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
          <Typography variant="subtitle1">
            Body <RequiredFieldMarker />
          </Typography>
          {hasPendingImageUploads && (
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="textSecondary">
                Uploading image…
              </Typography>
            </Stack>
          )}
        </Stack>
        <HtmlEditor
          key={state.formVersion}
          initialContent={articleState.htmlBody}
          onChange={(htmlBody) => setState(setHtmlBodyAction(htmlBody))}
          onUploadImages={onBodyImagesUpload}
          readOnly={isSaving}
          error={validationErrors.htmlBody !== undefined}
          editorStyles={{ minHeight: "36rem" }}
        />
      </Stack>

      {isPreviewOpen && (
        <InvestorCommunicationContentPreviewDialog onClose={() => setIsPreviewOpen(false)}>
          <InvestorCommunicationContentPreview
            title={state.title}
            categoryIds={state.categoryIds}
            htmlBody={articleState.htmlBody}
            heroImageSource={draftHeroImageSource}
          />
        </InvestorCommunicationContentPreviewDialog>
      )}
    </Stack>
  );
};

export default InvestorCommunicationArticleContentForm;
